import { Fragment, useRef } from "react";
import { Outlet } from "react-router";

import { t } from "@lingui/macro";
import { Copyright, Eye, Palette, PencilSimple, PlusCircle } from "@phosphor-icons/react";
import type {
  Award,
  Certification,
  CustomSection,
  Education,
  Experience,
  Interest,
  Language,
  Profile,
  Project,
  Publication,
  Reference,
  Skill,
  Volunteer,
} from "@reactive-resume/schema";
import { useBreakpoint } from "@reactive-resume/hooks";
import { Button, ScrollArea, Separator } from "@reactive-resume/ui";

import * as Tabs from "@radix-ui/react-tabs";
import { useResumeStore } from "@/client/stores/resume";

import { BuilderHeader } from "../../_components/header";
import { BuilderToolbar } from "../../_components/toolbar";

import { CssSection } from "../right/sections/css";
import { PageSection } from "../right/sections/page";
import { NotesSection } from "../right/sections/notes";
import { ThemeSection } from "../right/sections/theme";
import { ExportSection } from "../right/sections/export";
import { ImportSection } from "../right/sections/import";
import { LayoutSection } from "../right/sections/layout";
import { SharingSection } from "../right/sections/sharing";
import { TemplateSection } from "../right/sections/template";
import { StatisticsSection } from "../right/sections/statistics";
import { TypographySection } from "../right/sections/typography";

import { BasicsSection } from "./sections/basics";
import { SummarySection } from "./sections/summary";
import { SectionBase } from "./sections/shared/section-base";

// Renders the entire edit section present in the left side
export const LeftSidebar = () => {
  const containterRef = useRef<HTMLDivElement | null>(null);

  const { isDesktop } = useBreakpoint();

  const addSection = useResumeStore((state) => state.addSection);
  const customSections = useResumeStore((state) => state.resume.data.sections.custom);

  const scrollIntoView = (selector: string) => {
    const section = containterRef.current?.querySelector(selector);
    section?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Tabs.Root defaultValue="content" className="h-full w-full p-4">
      <Tabs.List className="sticky top-0 z-10 mx-auto mb-4 flex w-max gap-2 rounded-full border bg-gray-200/90 px-2 py-1 shadow-inner">
        <Tabs.Trigger
          value="content"
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border-transparent px-3 py-1.5 text-xs font-medium ring-offset-background transition-all hover:text-primary data-[state=active]:border-2 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-md"
        >
          <PencilSimple size={16} />
          Edit Content
        </Tabs.Trigger>
        <Tabs.Trigger
          value="format"
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border-transparent px-3 py-1.5 text-xs font-medium ring-offset-background transition-all hover:text-primary data-[state=active]:border-2 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-md"
        >
          <Palette size={16} />
          Edit Format
        </Tabs.Trigger>
        {!isDesktop && (
          <Tabs.Trigger
            value="preview"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border-transparent px-3 py-1.5 text-xs font-medium ring-offset-background transition-all hover:text-primary data-[state=active]:border-2 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-md"
          >
            <Eye size={16} />
            Preview
          </Tabs.Trigger>
        )}
      </Tabs.List>

      {/* "Edit Content" tab */}
      <Tabs.Content value="content" className="h-full w-full">
        <BuilderHeader />
        <ScrollArea type="scroll" orientation="vertical" className="force-scrollbar-visible h-[calc(100vh-5.2rem)] flex-1">
          <div ref={containterRef} className="grid gap-y-4 rounded-lg @container/left md:mx-4">
            {/* Basics */}
            <BasicsSection />

            {/* Summary */}
            <SummarySection />

            {/* Experience */}
            <SectionBase<Experience>
              id="experience"
              title={(item) => item.company}
              description={(item) => item.position}
            />

            {/* Education */}
            <SectionBase<Education>
              id="education"
              title={(item) => item.institution}
              description={(item) => item.area}
            />

            {/* Skills */}
            <SectionBase<Skill>
              id="skills"
              title={(item) => item.name}
              description={(item) => {
                if (item.description) return item.description;
                if (item.keywords.length > 0) return `${item.keywords.length} keywords`;
              }}
            />

            {/* Profile */}
            <SectionBase<Profile>
              id="profiles"
              title={(item) => item.network}
              description={(item) => item.username}
            />

            {/* Certifications */}
            <SectionBase<Certification>
              id="certifications"
              title={(item) => item.name}
              description={(item) => item.issuer}
            />

            {/* Languages */}
            <SectionBase<Language>
              id="languages"
              title={(item) => item.name}
              description={(item) => item.description}
            />

            {/* Awards */}
            <SectionBase<Award>
              id="awards"
              title={(item) => item.title}
              description={(item) => item.awarder}
            />

            {/* Interests */}
            <SectionBase<Interest>
              id="interests"
              title={(item) => item.name}
              description={(item) => {
                if (item.keywords.length > 0) return `${item.keywords.length} keywords`;
              }}
            />

            {/* Projects */}
            <SectionBase<Project>
              id="projects"
              title={(item) => item.name}
              description={(item) => item.description}
            />

            {/* Publications */}
            <SectionBase<Publication>
              id="publications"
              title={(item) => item.name}
              description={(item) => item.publisher}
            />

            {/* Volunteer */}
            <SectionBase<Volunteer>
              id="volunteer"
              title={(item) => item.organization}
              description={(item) => item.position}
            />

            {/* References */}
            <SectionBase<Reference>
              id="references"
              title={(item) => item.name}
              description={(item) => item.description}
            />

            {/* Custom Sections */}
            {Object.values(customSections).map((section) => (
              <Fragment key={section.id}>
                <SectionBase<CustomSection>
                  id={`custom.${section.id}`}
                  title={(item) => item.name}
                  description={(item) => item.description}
                />
              </Fragment>
            ))}

            <Button size="lg" variant="outline" onClick={addSection}>
              <PlusCircle />
              <span className="ml-2">{t`Add a new section`}</span>
            </Button>
          </div>
        </ScrollArea>
      </Tabs.Content>

      {/* "Edit Format" tab */}
      <Tabs.Content value="format" className="h-full w-full px-2">
        <BuilderHeader />
        <ScrollArea type="hover" orientation="vertical" hideScrollbar={true} className="h-[calc(100vh-5.2rem)] flex-1">
          <div ref={containterRef} className="grid gap-y-5 rounded-lg @container/right">
            <TemplateSection />

            <LayoutSection />

            <TypographySection />

            <ThemeSection />

            <CssSection />

            <PageSection />

            <SharingSection />

            <StatisticsSection />

            <ExportSection />

            <ImportSection />

            <NotesSection />

            {/* <InformationSection /> */}

            <Separator />
            <Copyright className="text-center" />
          </div>
        </ScrollArea>
      </Tabs.Content>

      {/* "Preview" tab - only for mobile screen size */}
      {!isDesktop && (
        <Tabs.Content value="preview" className="h-full w-full px-2">
          <BuilderHeader />
          <div className="absolute inset-0 flex items-center justify-center">
            <Outlet />
          </div>
          <div className="flex w-full items-center justify-center py-6">
            <BuilderToolbar />
          </div>
        </Tabs.Content>
      )}
    </Tabs.Root>
  );
};
